import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowUpRight, Moon, Sun } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn.ts';
import { requestMagicLink } from '../lib/auth.ts';
import { useSession } from '../lib/session.tsx';

// Kamilla's portrait asset is not yet in-repo (currently only in the bot).
// The left column renders a warm-dark atmospheric placeholder with a Cyrillic
// "К" monogram. When the portrait lands, swap the <PortraitPlaceholder />
// contents for the photo — surrounding layout stays.

function getInitialTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem('theme');
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function useLocalTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(getInitialTheme);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);
  const toggle = useCallback(
    () => setTheme((t) => (t === 'light' ? 'dark' : 'light')),
    [],
  );
  return { theme, toggle };
}

const emailLooksValid = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export function LandingRoute() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useSession();
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useLocalTheme();

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorNonce, setErrorNonce] = useState(0);

  useEffect(() => {
    if (!loading && user) {
      void navigate({ to: user.role === 'creator' ? '/creator' : '/student' });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (status === 'error') setErrorNonce((n) => n + 1);
  }, [status]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrorMsg(null);
    try {
      const callbackURL = `${window.location.origin}/auth/callback`;
      await requestMagicLink(email, callbackURL);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : t('landing.errorFallback'));
    }
  };

  const toggleLang = () => {
    const next = i18n.language.startsWith('ru') ? 'en' : 'ru';
    void i18n.changeLanguage(next);
  };

  const canSubmit = emailLooksValid(email) && status !== 'sending';

  return (
    <main
      id="landing-main"
      className="relative min-h-screen flex flex-col bg-bg text-fg overflow-hidden"
    >
      {/* Single ambient wash — one texture, per design principle "one earned moment". */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0"
        style={{
          background:
            'radial-gradient(ellipse 65% 50% at 18% 30%, var(--color-accent-soft), transparent 62%)',
        }}
      />

      {/* Quiet topbar */}
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5">
        <Link
          to="/"
          className="font-heading text-[15px] font-semibold tracking-tight text-fg focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring rounded-sm px-1"
        >
          {t('nav.brand')}
        </Link>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggleLang}
            className="h-9 px-3 rounded-lg text-[11px] font-mono tracking-[0.18em] uppercase text-fg-3 hover:text-fg hover:bg-bg-tint transition-colors duration-260 ease-[var(--ease-out-expo)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring cursor-pointer"
            aria-label="Toggle language"
          >
            {i18n.language.startsWith('ru') ? 'EN' : 'RU'}
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            className="inline-flex items-center justify-center size-9 rounded-lg text-fg-3 hover:text-fg hover:bg-bg-tint transition-colors duration-260 ease-[var(--ease-out-expo)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring cursor-pointer"
          >
            {theme === 'light' ? <Moon className="size-4" /> : <Sun className="size-4" />}
          </button>
        </div>
      </header>

      {/* Two-column hero */}
      <div className="relative z-10 flex-1 grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-stretch">
        <PortraitPlaceholder
          caption={t('landing.portraitCaption')}
          alt={t('studentDashboard.kamillaPortraitAlt')}
        />

        <section className="landing-reveal-auth flex flex-col justify-center px-6 sm:px-10 xl:px-14 py-12 xl:py-0">
          <div className="max-w-[460px] w-full">
            <h1
              className="font-heading font-semibold text-fg mb-9"
              style={{
                fontSize: 'clamp(30px, 3.6vw, 44px)',
                lineHeight: 1.08,
                letterSpacing: '-0.025em',
              }}
            >
              {status === 'sent' ? t('landing.heroSent') : t('landing.hero')}
            </h1>

            {/* Reserved height so state swap doesn't shift layout */}
            <div className="min-h-[130px]">
              {status === 'sent' ? (
                <div
                  className="text-fg-2 text-[15px] leading-relaxed"
                  role="status"
                  aria-live="polite"
                  data-testid="magic-link-sent"
                >
                  {t('landing.sentDetail', { email })}
                </div>
              ) : (
                <form
                  key={errorNonce}
                  onSubmit={onSubmit}
                  className={cn(status === 'error' && 'landing-shake')}
                  noValidate
                >
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                    <label htmlFor="email" className="sr-only">
                      {t('landing.emailLabel')}
                    </label>
                    <input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      required
                      autoComplete="email"
                      inputMode="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (status === 'error') setStatus('idle');
                      }}
                      placeholder={t('landing.placeholder')}
                      className="flex-1 min-w-0 h-12 px-4 rounded-xl bg-bg-card/80 border border-border text-fg text-[15px] font-body placeholder:text-fg-3 transition-all duration-260 ease-[var(--ease-out-expo)] focus-visible:outline-none focus-visible:border-accent focus-visible:ring-3 focus-visible:ring-ring"
                    />
                    <button
                      type="submit"
                      data-testid="send-magic-link"
                      disabled={!canSubmit}
                      className="grad-accent h-12 px-5 rounded-xl inline-flex items-center justify-center gap-2 text-white text-[14px] font-heading font-semibold shadow-flat hover:shadow-hover transition-all duration-260 ease-[var(--ease-out-expo)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring disabled:opacity-50 disabled:pointer-events-none active:scale-[0.97] cursor-pointer whitespace-nowrap"
                    >
                      {status === 'sending' ? (
                        <span className="inline-flex items-center gap-2">
                          <span
                            aria-hidden
                            className="size-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin"
                          />
                          {t('landing.sending')}
                        </span>
                      ) : (
                        <>
                          <span>{t('landing.submit')}</span>
                          <ArrowUpRight className="size-4" strokeWidth={2.5} aria-hidden />
                        </>
                      )}
                    </button>
                  </div>
                  {status === 'error' && errorMsg ? (
                    <p
                      className="text-[13px] mt-3"
                      style={{ color: 'var(--color-error)' }}
                      role="alert"
                      aria-live="polite"
                      data-testid="magic-link-error"
                    >
                      {errorMsg}
                    </p>
                  ) : null}
                </form>
              )}
            </div>

            <p className="landing-reveal-helpline text-fg-3 text-[13px] mt-10 leading-relaxed">
              {t('landing.helpLine')}{' '}
              <a
                href="https://t.me/amirkamilla"
                target="_blank"
                rel="noreferrer"
                className="text-fg-2 font-body font-semibold underline-offset-[3px] decoration-fg-3/60 hover:text-fg hover:decoration-fg-2 hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring rounded-sm"
              >
                {t('landing.helpLineHandle')}
              </a>{' '}
              {t('landing.helpLineTail')}
            </p>
          </div>
        </section>
      </div>

      <footer className="relative z-10 px-5 sm:px-8 py-5 text-center text-fg-3 text-[10px] font-mono tracking-[0.28em] uppercase">
        {t('landing.finePrint')}
      </footer>
    </main>
  );
}

function PortraitPlaceholder({ caption, alt }: { caption: string; alt: string }) {
  return (
    <figure
      aria-label={alt}
      className="landing-reveal-portrait relative flex items-end min-h-[44vh] xl:min-h-0 xl:h-[calc(100vh-80px)] overflow-hidden"
    >
      {/* Warm atmospheric field — studio-portrait lighting. Theme-aware:
          warm-dark in dark mode, warm peach in light mode. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 70% at 45% 28%, var(--color-portrait-highlight), var(--color-portrait-bg) 68%, var(--color-portrait-shadow))',
        }}
      />
      {/* Warm key light */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 45% 36% at 38% 24%, var(--color-accent-mid), transparent 62%)',
        }}
      />
      {/* Film grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,%3Csvg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />
      {/* Monogram К — low opacity, asymmetric placement */}
      <div aria-hidden className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-heading font-bold select-none"
          style={{
            fontSize: 'clamp(14rem, 32vw, 28rem)',
            letterSpacing: '-0.07em',
            lineHeight: 0.9,
            color: 'var(--color-accent)',
            opacity: 0.09,
            transform: 'translate(-7%, -8%)',
          }}
        >
          К
        </span>
      </div>
      <figcaption
        className="relative z-10 w-full px-5 sm:px-8 pb-5 sm:pb-8 text-[10px] font-mono tracking-[0.28em] uppercase"
        style={{ color: 'var(--color-portrait-caption)' }}
      >
        {caption}
      </figcaption>
    </figure>
  );
}
